import type { SenderInfo } from "@gsignatures/core";
import { useEffect, useRef, useState } from "react";
import { who } from "../lib/format";
import type { SaveStatus } from "../state/saver";
import { S } from "../strings";
import { BrandMark } from "./BrandMark";
import { Icon, type IconName } from "./Icon";

export type MenuAction = "paper" | "decline" | "download";

interface Props {
  sender: SenderInfo | null;
  /** 0 = verify … 4 = review & sign, 5 = done; null hides the stepper. */
  stepIndex: number | null;
  save?: SaveStatus;
  onHelp?: (() => void) | undefined;
  /** Present while the menu applies (consent to signing). */
  onMenu?: ((a: MenuAction) => void) | undefined;
}

const SAVE_TEXT: Partial<Record<SaveStatus, string>> = {
  pending: S.top.saving,
  saving: S.top.saving,
  saved: S.top.saved,
  retrying: S.top.retrying,
  failed: S.top.failed,
};

export function TopBar({ sender, stepIndex, save = "idle", onHelp, onMenu }: Props) {
  const idx = stepIndex;
  const showSteps = idx !== null && idx >= 0 && idx < 5;
  const cur = showSteps ? S.steps[Math.min(idx, 4)] : null;
  const saveText = SAVE_TEXT[save];
  const saveTone = save === "retrying" || save === "failed" ? "bad" : save === "saved" ? "ok" : "busy";
  return (
    <header className="topbar">
      <div className="topbar-in">
        <div className="brand">
          {sender ? <BrandMark name={sender.name} color={sender.color} logoUrl={sender.logoUrl} /> : <div className="brand-mark neutral" aria-hidden="true"><Icon n="pen" /></div>}
          <div className="brand-text">
            <div className="brand-name">{sender ? sender.name : S.product}</div>
            <div className="brand-sub">
              <Icon n="lock" /> {S.securedBy}
            </div>
            {cur && idx !== null && (
              <div className="brand-step" aria-hidden="true">
                {S.stepOf(idx + 1, 5)} · <b>{cur}</b>
              </div>
            )}
          </div>
        </div>
        {showSteps ? (
          <>
            <nav className="stepper" aria-label={S.progressNav}>
              <ol>
                {S.steps.map((label, i) => (
                  <li key={label} className="step-item" aria-current={i === idx ? "step" : undefined}>
                    {i > 0 && <span className="step-line" aria-hidden="true" />}
                    <span className={`step ${i < idx ? "done" : i === idx ? "now" : ""}`}>
                      <span className="dot" aria-hidden="true">
                        {i < idx ? <Icon n="check" /> : i + 1}
                      </span>
                      <span className={i === idx ? "step-label" : "step-label step-label-hide"}>
                        {label}
                        {i < idx && <span className="sr"> (done)</span>}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            </nav>
            <div className="step-compact" aria-hidden="true">
              <b>{cur}</b>
              <small>{S.stepOf(idx + 1, 5)}</small>
            </div>
          </>
        ) : (
          <div className="spacer" />
        )}
        <div className="top-right">
          {saveText && (
            <div className={`saved ${saveTone}`} title={S.top.savedTitle}>
              <i aria-hidden="true" />
              <span>{saveText}</span>
            </div>
          )}
          {onHelp && (
            <button type="button" className="icon-btn" onClick={onHelp} aria-label={S.top.help}>
              <Icon n="help" />
              <span className="hide-phone" aria-hidden="true">{S.top.help}</span>
            </button>
          )}
          {onMenu && sender && <Menu onPick={onMenu} contactFirst={who(sender).contactFirst} sender={sender} />}
        </div>
      </div>
    </header>
  );
}

function Menu({ onPick, sender }: { onPick: (a: MenuAction) => void; contactFirst: string; sender: SenderInfo }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const items: { a: MenuAction; icon: IconName; title: string; sub: string }[] = [
    { a: "paper", icon: "paper", title: S.top.menuPaper, sub: S.top.menuPaperSub },
    { a: "decline", icon: "x", title: S.top.menuDecline, sub: S.top.menuDeclineSub(who(sender)) },
    { a: "download", icon: "download", title: S.top.menuDownload, sub: S.top.menuDownloadSub },
  ];

  useEffect(() => {
    if (!open) return;
    const first = wrap.current?.querySelector<HTMLElement>('[role="menuitem"]');
    first?.focus();
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const onKey = (e: React.KeyboardEvent) => {
    const list = [...(wrap.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const i = list.indexOf(document.activeElement as HTMLElement);
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      btn.current?.focus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      list[(i + 1) % list.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      list[(i - 1 + list.length) % list.length]?.focus();
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  return (
    <div className="menu-wrap" ref={wrap} onKeyDown={onKey}>
      <button ref={btn} type="button" className="icon-btn" aria-label={S.top.more} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        <Icon n="more" />
      </button>
      {open && (
        <div className="menu" role="menu" aria-label={S.top.more}>
          {items.map((it) => (
            <button
              key={it.a}
              type="button"
              role="menuitem"
              tabIndex={-1}
              onClick={() => {
                setOpen(false);
                onPick(it.a);
              }}
            >
              <Icon n={it.icon} />
              <span>
                <b>{it.title}</b>
                <small>{it.sub}</small>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
